<template>
  <div class="flex-align gap2 justify-end mb-4">
    <AppActions :actions="actions" namespace="event-attendees" elevation="1" />
  </div>

  <DataTable v-bind="ticketsTable" :loading="loading" :items="tickets">
    <template #before>
      <div class="form-section-heading px-4 mt-2">Attendees</div>
      <v-divider />
    </template>
  </DataTable>

  <DataTable
    class="mt-4"
    v-bind="discountsTable"
    :loading="loading"
    :items="discounts"
  >
    <template #before>
      <div class="flex-align justify-between px-4 my-2">
        <div class="form-section-heading">Discount links</div>
        <AppActions namespace="event-discounts" :actions="discountsActions" />
      </div>
      <v-divider />
    </template>
  </DataTable>

  <EditEventTicket />

  <Exporter
    title="Export attendees"
    name="event_attendees"
    actions-namespace="event-attendees"
    action="attendees:export"
    :args="exportArgs"
    hide-date-range
  />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { first, get, kebabCase, omit } from "lodash";
import { jsPDF } from "jspdf";
import type { autoTable, RowInput } from "jspdf-autotable";
import "jspdf-autotable";
import type Event from "~/models/dobby/Event";
import { Rule } from "~/services/Rules";
import type { IEventDiscount } from "~/models/interfaces/Event";
import { EventDiscount } from "~/models/dobby/EventDiscount";
import ManualTicketInput from "./sections/ManualTicketInput.vue";
import EditEventTicket from "./sections/EditEventTicket.vue";
import { useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { useToast } from "vue-toastification";
import type { TicketItem } from "./sections/types";
import { TextCopy } from "#components";
import type { QuantityListInputSelection } from "~/components/inputs/QuantityListInput.vue";
import QuantityListInput from "~/components/inputs/QuantityListInput.vue";

export default defineComponent({
  components: { EditEventTicket },
  setup() {
    const { handle: handleAttendees } = useNamespacedEvent("event-attendees");
    const { handle: handleDiscounts } = useNamespacedEvent("event-discounts");
    const { original, data, loading, refetch } = useSaveContext<Event>();
    const { wrapConfirmInput } = useConfirm();
    const { wrapLoading } = useLoading();
    const cachedDiscounts = shallowRef<IEventDiscount[]>([]);
    const toast = useToast();

    const tickets = computed(() => {
      const bookings = data.value.bookings || [];
      const items = bookings.map((booking): TicketItem | void => {
        const ticket = first(booking.tickets);
        if (ticket) {
          return {
            id: ticket.id,
            created_at: ticket.created_at,
            name: ticket.name,
            attendees: execFn(() => {
              const len = booking.tickets?.length || 0;
              if (len > 1) {
                return mapBy(booking.tickets, "name").join(", ");
              }
            }),
            ticket: ticket.eventTicket.name,
            code: ticket.code,
            email: ticket.email,
            phone: ticket.attendee?.phone,
            displaySeats: ticket.displaySeats,
            ticketsCount: booking.tickets?.length || 0,
            order: booking.order,
            booking,
            manual: !booking.order,
          };
        }
      });

      // sort by created_at descending
      return (items.filter((v) => !!v) as TicketItem[]).sort(
        (a: TicketItem, b: TicketItem) => {
          if (!a.created_at || !b.created_at) return 0;
          return parseDateLike(b.created_at).isBefore(a.created_at)
            ? -1
            : parseDateLike(b.created_at).isAfter(a.created_at)
            ? 1
            : 0;
        }
      );
    });

    const discounts = computed(() => {
      const o = toRaw(original.value.discounts || []);
      const c = toRaw(cachedDiscounts.value);
      return [...o, ...c];
    });

    const exportArgs = computed(() => {
      return {
        event: original.value.handle,
      };
    });

    const createManualTicketMutation = useMutation<{
      name: string;
      email: string;
      phone?: string;
      send_email?: boolean;
      tickets: {
        id: string;
        quantity?: number;
        seats?: string[];
      }[];
    }>(gql`
      mutation ($data: EventManualTicketCreateInput!) {
        wsCreateManualTicket(data: $data) {
          code
        }
      }
    `);

    const getTickets = () => {
      return toRaw(original.value.sections)
        .map((section) => section.tickets)
        .flat();
    };

    const createDiscount = wrapConfirmInput(
      wrapLoading(async (input) => {
        const inputTickets: QuantityListInputSelection[] = input.tickets;

        let occurrence: string | undefined;
        if (original.value.occurrences.length > 1) {
          const section = original.value.sections.find((x) => {
            if (x.tickets) {
              const tickets = x.tickets.map((t) => t.gid);
              return !!inputTickets.find((ticket) =>
                tickets.includes(ticket.value)
              );
            }
          });
          occurrence = section?.occurrence_id;
        } else {
          occurrence = get(original.value.occurrences, "[0].id");
        }

        const model = await EventDiscount.create({
          ...input,
          event_occurrence: occurrence,
          tickets: inputTickets.map((selection) => ({
            gid: selection.value,
            quantity: selection.quantity,
          })),
        });

        toast.success("Discount link created");

        cachedDiscounts.value = [
          ...cachedDiscounts.value,
          model.$toJson() as any,
        ];

        refetch().then(() => {
          cachedDiscounts.value = [];
        });
      }),
      () => {
        const tickets = getTickets();
        // const reservedSeating = execFn(() => {
        //   const occurrences = original.value.occurrences || [];
        //   return someTrue(mapBy(occurrences, "reserved_seating"));
        // });

        return {
          title: "Add a new discount link",
          doneText: "Save",
          inputs: defineFormInputs({
            name: {
              name: "Name",
              rules: Rule.group([Rule.required(), Rule.title()]),
              type: "text",
              props: {
                hint: "The guest's name. Also used when emailing the guest",
                persistentHint: true,
              },
            },
            email: {
              name: "Email",
              rules: Rule.group([Rule.required(), Rule.email()]),
              type: "text",
            },
            // send_email: {
            //   hideName: true,
            //   component: CheckBox,
            //   props: {
            //     title: "Send magic link to the provided email",
            //     class: "mb-2",
            //   },
            // },
            // send_text: {
            //   hideName: true,
            //   component: CheckBox,
            //   props: {
            //     title: "Text magic link to the provided phone",
            //     class: "mb-2",
            //   },
            // },
            tickets: {
              name: "Tickets",
              component: QuantityListInput,
              rules: Rule.required(),
              props: {
                // returnObject: true,
                items: tickets,
                itemTitle: "name",
                itemValue: "gid",
              },
            },
            // allow_reserved_seats: {
            //   hideName: true,
            //   component: CheckBox,
            //   hidden: !reservedSeating,
            //   props: {
            //     title: "Allow reserved seats",
            //     subtitle:
            //       "Allow the guest to pick reserved seats with this discount.",
            //     class: "mb-2",
            //   },
            // },
          }),
          inputsDefaults: {
            tickets: [],
            allow_reserved_seats: false,
          },
        };
      }
    );

    const createManualTicket = wrapConfirmInput(
      wrapLoading(async ({ input }) => {
        const data = omit(Object.assign({}, input), ["occurrence", "tickets"]);

        data.tickets = wrap(input.tickets).map(
          (selection: QuantityListInputSelection) => ({
            gid: selection.value,
            quantity: selection.quantity,
            seats: [],
          })
        );

        toast.info("Creating manual ticket...");
        createManualTicketMutation
          .mutate({ data })
          .then(() => {
            toast.success("Ticket created");
            refetch();
          })
          .catch(() => {
            toast.error("Failed to create manual ticket");
          });
      }),
      () => {
        return {
          title: "Create manual ticket",
          doneText: "Save",
          width: "550px",
          inputs: defineFormInputs({
            input: {
              hideName: true,
              component: ManualTicketInput,
              rules: Rule.required(),
            },
          }),
          inputsDefaults: {
            input: {},
          },
        };
      }
    );

    const print = () => {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "in",
        format: "letter",
      });

      const heading = `${original.value.name} Attendees`;
      doc.setFontSize(16).text(heading, 0.5, 1.0);
      doc.setLineWidth(0.01).line(0.5, 1.1, 8.0, 1.1);
      (doc as jsPDF & { autoTable: autoTable }).autoTable({
        // TODO: set header color to (255, 165, 0)
        columns: [
          { title: "Name", dataKey: "name" },
          { title: "Ref", dataKey: "code" },
          { title: "Seats", dataKey: "displaySeats" },
          { title: "Email", dataKey: "email" },
        ],
        body: tickets.value.sort((a, b) =>
          a.name.localeCompare(b.name)
        ) as RowInput[],
        margin: { left: 0.5, top: 1.25 },
      });

      // doc
      //   .setFont("helvetica")
      //   .setFontSize(12)
      //   .text(this.moreText, 0.5, 3.5, { align: "left", maxWidth: "7.5" });

      const filename = `${kebabCase(heading)}.pdf`;

      doc
        .setFont("times")
        .setFontSize(11)
        // .setFontStyle("italic")
        .setTextColor(100, 100, 100)
        .text(
          "Generated " + new Date(),
          0.5,
          doc.internal.pageSize.height - 0.5
        )
        .save(filename);

      toast.success(`Attendees printed (${filename})`);
    };

    handleDiscounts("add:discount", createDiscount);
    handleAttendees("add:manual-ticket", createManualTicket);
    handleAttendees("attendees:print", print);

    return {
      original,
      tickets,
      discounts,
      loading,
      exportArgs,
    };
  },
  data: () => ({
    actions: defineActions([
      {
        title: "Create manual ticket",
        key: "add:manual-ticket",
        icon: "heroicons:plus",
      },
      {
        title: "Export",
        key: "attendees:export",
        icon: "carbon:export",
      },
      {
        title: "Quick print",
        key: "attendees:print",
        icon: "heroicons:printer",
      },
    ]),
    ticketsTable: defineTable({
      showSelect: false,
      searchable: true,
      configurable: true,
      applyFilters: true,
      actionsNamespace: "event-attendees",

      emptyIcon: "heroicons:ticket",
      emptyTitle: "Ticket bookings will show here",
      emptyDescription:
        "This is where you’ll manage your event attendees. You can create tickets to add or invite special guests to this event.",

      views: [
        { id: "all", title: "All" },
        // { id: "unpaid", title: "Unpaid", filters: tableFilter({ order: null }) },
        {
          id: "manual",
          title: "Manual tickets",
          filters: tableFilter({ manual: true }),
        },
        {
          id: "ordered",
          title: "Ordered tickets",
          filters: tableFilter({ manual: false }),
        },
        // { id: "cancelled", title: "Cancelled", filters: tableFilter({ order: { $exists: false } }) },
        // { id: "refunded", title: "Refunded", filters: tableFilter({ order: { $exists: false } }) },
        // { id: "expired", title: "Expired", filters: tableFilter({ order: { $exists: false } }) },
        // { id: "pending", title: "Pending", filters: tableFilter({ order: { $exists: false } }) },
        // { id: "completed", title: "Completed", filters: tableFilter({ order: { $exists: false } }) },
        // { id: "failed", title: "Failed", filters: tableFilter({ order: { $exists: false } }) },
      ],

      filters: {
        created_at: "date",
        order: "float",
        manual: {
          name: "Manual tickets",
          type: "boolean",
        },
      },

      columns: [
        { title: "Date", sortable: true, key: "created_at", type: "date" },
        { title: "Name", sortable: true, key: "name" },
        { title: "Ref.", sortable: true, key: "code", type: "title" },
        { title: "Ticket", sortable: true, key: "ticket" },
        { title: "Email", sortable: true, key: "email", type: "email" },
        { title: "Seats", sortable: false, key: "displaySeats" },
        { title: "Tickets", sortable: true, key: "ticketsCount" },
        { title: "Order", sortable: false, key: "order", type: "resource" },
      ],
    }),
    discountsTable: defineTable({
      actionsNamespace: "event-discounts",
      searchable: true,
      applyFilters: true,

      emptyIcon: "streamline:discount-percent-badge",
      emptyTitle: "Special discounts",
      emptyDescription:
        "You can use discounts to offer special tickets to your attendees.",

      views: [
        { id: "all", title: "All" },
        {
          id: "used",
          title: "Used",
          filters: tableFilter({ used: true }),
        },
        {
          id: "unused",
          title: "Unused",
          filters: tableFilter({ used: false }),
        },
      ],

      columns: [
        { title: "Name", sortable: true, key: "name" },
        {
          title: "Link",
          sortable: true,
          key: "web_url",
          component: dataTableComponent(TextCopy, {
            usePropKey: "text",
            useItemValue: true,
            props: {
              class: "md:max-w-350px",
            },
          }),
        },
        { title: "Email", sortable: true, key: "email", type: "email" },
        { title: "Used", sortable: false, key: "used", type: "boolean" },
        { title: "Tickets", sortable: false, key: "tickets", type: "count" },
      ],
    }),
    discountsActions: defineActions([
      {
        title: "Add discount",
        key: "add:discount",
        icon: "heroicons:plus",
      },
    ]),
  }),
});
</script>
